import React, {useContext, useEffect, useMemo, useRef, useState} from "react";
import axios from "axios";
import {Checkbox, MessageBar, PrimaryButton, Spinner, Stack, Text} from "@fluentui/react";
import {EInvoiceSettingsDto} from "../API";
import {AuthContext} from "../contexts/AuthContext";

export const EInvoiceSettingsForm = ({companyId, crossCompany = false, onSaved}: {
    companyId: number; crossCompany?: boolean; onSaved?: () => void;
}) => {
    const {Token} = useContext(AuthContext);
    const client = useMemo(() => axios.create({headers: {Authorization: `Bearer ${Token}`}}), [Token]);
    const scope = useMemo(() => ({companyId, crossCompany, client}), [companyId, crossCompany, client]);
    const current = useRef(scope); current.current = scope;
    const [settings, setSettings] = useState<EInvoiceSettingsDto>();
    const [busy, setBusy] = useState(false);
    const [message, setMessage] = useState("");
    useEffect(() => {
        const request = new AbortController();
        setSettings(undefined); setBusy(true); setMessage("");
        void client.get<EInvoiceSettingsDto>("/api/einvoice/settings", {params: {companyId, crossCompany}, signal: request.signal})
            .then(({data}) => { if (current.current === scope) setSettings(data); })
            .catch(() => { if (!request.signal.aborted && current.current === scope) setMessage("Nije moguće dohvatiti postavke eRačuna."); })
            .finally(() => { if (current.current === scope) setBusy(false); });
        return () => request.abort();
    }, [client, companyId, crossCompany, scope]);

    const save = async () => {
        if (!settings || busy || (settings.onixPhoneEInvoiceEnabled && settings.eInvoiceIssuerVatRegistered !== true)) return;
        setBusy(true); setMessage("");
        try {
            const {data} = await client.put<EInvoiceSettingsDto>("/api/einvoice/settings", settings, {params: {companyId, crossCompany}});
            if (current.current === scope) { setSettings(data); setMessage("Postavke eRačuna su spremljene."); onSaved?.(); }
        } catch {
            if (current.current === scope) setMessage("Postavke nisu spremljene. Provjerite PDV potvrdu, zajednički URA ugovor i radnik za obradu dokumenata.");
        } finally { if (current.current === scope) setBusy(false); }
    };

    return <Stack tokens={{childrenGap: 12}} styles={{root: {padding: 12}}}>
        <Text variant="large">eRačuni poduzeća {companyId}</Text>
        <Text>Vrijedi za sadašnje domaće B2B račune PDV izdavatelja, plaćene preko transakcijskog računa. Na pojedinoj vrsti računa uključite slanje B2B eRačuna.</Text>
        <Text>Koristi se postojeći zajednički URA postupak slanja. PDF iz OnixPhonea obavezno se pohranjuje i ugrađuje u XML. Gotovina i kartice zadržavaju F1.</Text>
        {busy ? <Spinner label="Postavke eRačuna" /> : null}
        {message ? <MessageBar>{message}</MessageBar> : null}
        {settings ? <>
            <Checkbox label="Potvrđujem da je izdavatelj u sustavu PDV-a" checked={settings.eInvoiceIssuerVatRegistered === true}
                disabled={busy} onChange={(_, checked) => setSettings({...settings, eInvoiceIssuerVatRegistered: checked ?? false})} />
            <Checkbox label="Automatski šalji domaće B2B eRačune iz OnixPhonea" checked={settings.onixPhoneEInvoiceEnabled}
                disabled={busy} onChange={(_, checked) => setSettings({...settings, onixPhoneEInvoiceEnabled: checked ?? false})} />
            <PrimaryButton text="Spremi postavke eRačuna" disabled={busy || (settings.onixPhoneEInvoiceEnabled && settings.eInvoiceIssuerVatRegistered !== true)}
                onClick={() => { void save(); }} />
        </> : null}
        <Text>Isključivanje zaustavlja nove automatske račune, ne oporavak već izdanih. Za pauziranje postojećeg računa koristite nadzirani F2 oporavak.</Text>
        <Text>Automatski eReporting se ne provodi. Potreban ručni postupak, uključujući nedostupan AMS, nije izvršen ovom funkcijom.</Text>
    </Stack>;
};
